import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import useSWR, { useSWRConfig } from 'swr';
import {
  useTodos,
  Todo,
  useRecurrenceCompletions,
  markRecurrenceComplete,
  markRecurrenceIncomplete,
} from './useData';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Switch } from '../../components/ui';
import { updateTodo, updateAppointment } from '../../services/mutations';
import { formatDateTime } from '../../config/appConfig';
import { fetcher } from '../../services/fetcher';
import type { Appointment } from '../crm-appointments/useData';
import { TodoTypeIcon } from './todosCalendarShared';
import { getTodosForDay, formatAppointmentTimeRange } from './todosCalendarUtils';
import { setDragData, getDragData, type DraggedItem } from './todosCalendarDragDrop';

dayjs.extend(isoWeek);

export interface TodosWeeklyViewProps {
  currentDate?: Dayjs;
  setCurrentDate?: (d: Dayjs | ((prev: Dayjs) => Dayjs)) => void;
  showOnlyOpen?: boolean;
  showAppointments?: boolean;
  setShowAppointments?: (value: boolean) => void;
  embeddedInCalendar?: boolean;
}

const weekDayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TodosWeeklyView: React.FC<TodosWeeklyViewProps> = (props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { colors } = useTheme();
  const [internalDate, setInternalDate] = useState<Dayjs>(dayjs());
  const currentDate = props.currentDate ?? internalDate;
  const setCurrentDate = props.setCurrentDate ?? setInternalDate;
  const showOnlyOpen = props.showOnlyOpen === true;
  const [internalShowAppointments, setInternalShowAppointments] = useState(false);
  const showAppointments = props.showAppointments ?? internalShowAppointments;
  const setShowAppointments = props.setShowAppointments ?? setInternalShowAppointments;
  const SPLITTER_STORAGE_KEY = 'todos-calendar-weekly-splitter';
  const [splitterPosition, setSplitterPosition] = useState(() => {
    try {
      const v = sessionStorage.getItem(SPLITTER_STORAGE_KEY);
      if (v != null) {
        const n = Number(v);
        if (Number.isFinite(n) && n >= 30 && n <= 80) return n;
      }
    } catch {
      // ignore
    }
    return 65;
  });
  const embeddedInCalendar = props.embeddedInCalendar === true;

  const weekStart = currentDate.startOf('isoWeek');
  const weekEnd = weekStart.add(6, 'day');
  const month1 = weekStart.format('YYYY-MM');
  const month2 = weekEnd.format('YYYY-MM');

  const { mutate: globalMutate } = useSWRConfig();
  const { data: todos, isLoading, isError, mutate: mutateTodos } = useTodos();
  const appointmentsUrl =
    showAppointments && user?.id ? `/crm-clients-appointments/all_of_user/${user.id}` : null;
  const { data: appointmentsData, mutate: mutateAppointments } = useSWR<Appointment[] | null>(appointmentsUrl, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });
  const allAppointments: Appointment[] = Array.isArray(appointmentsData) ? appointmentsData : [];

  // Revalidate when view becomes visible (e.g. after returning from todo/appointment add or edit)
  useEffect(() => {
    const onVisible = () => {
      globalMutate((key: string) => typeof key === 'string' && (key.startsWith('/crm-todos') || key.startsWith('/crm-clients-appointments')));
    };
    if (document.visibilityState === 'visible') onVisible();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [globalMutate]);

  const { completions: completions1, mutate: mutateCompletions1 } = useRecurrenceCompletions(month1);
  const { completions: completions2, mutate: mutateCompletions2 } = useRecurrenceCompletions(month1 === month2 ? null : month2);
  const completions = useMemo(
    () => (month1 === month2 ? completions1 || [] : [...(completions1 || []), ...(completions2 || [])]),
    [month1, month2, completions1, completions2]
  );

  const completionSet = useMemo(
    () => new Set((completions || []).map((c) => `${c.todoId}-${c.occurrenceDate}`)),
    [completions]
  );
  const completionByKey = useMemo(() => {
    const map = new Map<string, (typeof completions)[0]>();
    (completions || []).forEach((c) => map.set(`${c.todoId}-${c.occurrenceDate}`, c));
    return map;
  }, [completions]);

  const isRecurrenceCompleted = (todoId: number, day: Dayjs) =>
    completionSet.has(`${todoId}-${day.format('YYYY-MM-DD')}`);
  const getRecurrenceCompletion = (todoId: number, day: Dayjs) =>
    completionByKey.get(`${todoId}-${day.format('YYYY-MM-DD')}`);

  const recurringTodos = useMemo(
    () => (todos || []).filter((t) => t.todoRecurring && t.todoRecurrenceInterval),
    [todos]
  );
  const nonRecurringTodos = useMemo(
    () => (todos || []).filter((t) => !t.todoRecurring || !t.todoRecurrenceInterval),
    [todos]
  );
  const unscheduledTodos = useMemo(() => {
    const list = (todos || []).filter((t) => {
      if (t.todoDueDate || (t.todoRecurring && t.todoRecurrenceInterval)) return false;
      if (!showOnlyOpen) return true;
      return !t.todoCompleted;
    });
    return list.sort((a, b) => Number(a.todoCompleted) - Number(b.todoCompleted));
  }, [todos, showOnlyOpen]);

  const appointmentsInWeek = useMemo(() => {
    if (!showAppointments || allAppointments.length === 0) return [];
    return allAppointments.filter((a) => {
      if (!a.clientAppScheduleDateTime) return false;
      if (showOnlyOpen && a.clientAppStatus === 2) return false;
      const d = dayjs(a.clientAppScheduleDateTime);
      return !d.isBefore(weekStart, 'day') && !d.isAfter(weekEnd, 'day');
    });
  }, [showAppointments, allAppointments, weekStart, weekEnd, showOnlyOpen]);

  const getAppointmentsForDay = (day: Dayjs): Appointment[] =>
    appointmentsInWeek.filter((a) => {
      if (!a.clientAppScheduleDateTime) return false;
      return dayjs(a.clientAppScheduleDateTime).isSame(day, 'day');
    });

  const getTodosForDayLocal = (day: Dayjs) =>
    getTodosForDay(recurringTodos, nonRecurringTodos, day);

  type DayItem =
    | { type: 'todo'; todo: Todo; day: Dayjs }
    | { type: 'appointment'; appointment: Appointment };
  const getDayItems = (day: Dayjs): DayItem[] => {
    let dayTodos = getTodosForDayLocal(day).map((todo) => ({ type: 'todo' as const, todo, day }));
    const dayAppointments = showAppointments
      ? getAppointmentsForDay(day).map((appointment) => ({ type: 'appointment' as const, appointment }))
      : [];

    if (showOnlyOpen) {
      dayTodos = dayTodos.filter(({ todo, day: itemDay }) => {
        const isRecurring = !!todo.todoRecurring && !!todo.todoRecurrenceInterval;
        const occurrenceDone = isRecurring && isRecurrenceCompleted(todo.todoId, itemDay);
        const completion = isRecurring ? getRecurrenceCompletion(todo.todoId, itemDay) : null;
        const isDone =
          occurrenceDone ||
          todo.todoCompleted ||
          (!!completion?.completedAt && !!completion?.completedUserDisplayName);
        return !isDone;
      });
    }

    const items: DayItem[] = [...dayTodos, ...dayAppointments];

    items.sort((a, b) => {
      const timeA =
        a.type === 'appointment' && a.appointment.clientAppScheduleDateTime
          ? dayjs(a.appointment.clientAppScheduleDateTime).valueOf()
          : 0;
      const timeB =
        b.type === 'appointment' && b.appointment.clientAppScheduleDateTime
          ? dayjs(b.appointment.clientAppScheduleDateTime).valueOf()
          : 0;
      return timeA - timeB;
    });

    return items;
  };

  const weekDays: Dayjs[] = useMemo(() => {
    const out: Dayjs[] = [];
    for (let i = 0; i < 7; i++) out.push(weekStart.add(i, 'day'));
    return out;
  }, [weekStart]);

  const handlePrevious = () => setCurrentDate((d) => d.subtract(1, 'week'));
  const handleNext = () => setCurrentDate((d) => d.add(1, 'week'));
  const handleToday = () => setCurrentDate(dayjs().startOf('day'));
  const handleNewTodo = () => navigate('/todos/new');
  const handleTodoClick = (todo: Todo) => navigate(`/todos/${todo.todoId}`);

  const handleToggleComplete = async (todo: Todo, completed: boolean) => {
    try {
      await updateTodo(todo.todoId, { todoCompleted: completed });
    } catch {
      mutateTodos();
    }
  };

  const handleToggleRecurrenceComplete = async (todo: Todo, day: Dayjs, completed: boolean) => {
    const dateStr = day.format('YYYY-MM-DD');
    try {
      if (completed) {
        await markRecurrenceComplete(todo.todoId, dateStr);
      } else {
        await markRecurrenceIncomplete(todo.todoId, dateStr);
      }
      mutateTodos();
    } catch {
      mutateTodos();
    }
  };

  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [dragOverDay, setDragOverDay] = useState<Dayjs | null>(null);
  const [dragOverSupportTaskZone, setDragOverSupportTaskZone] = useState(false);

  const handleDragStart = (item: DraggedItem, e: React.DragEvent) => {
    setDragData(e, item);
    setDraggedItem(item);
    if (e.currentTarget instanceof HTMLElement) e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedItem(null);
    setDragOverDay(null);
    setDragOverSupportTaskZone(false);
    if (e.currentTarget instanceof HTMLElement) e.currentTarget.style.opacity = '1';
  };

  const handleDropToCreateSupportTask = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSupportTaskZone(false);
    const payload = getDragData(e);
    if (!payload || payload.type !== 'todo') {
      setDraggedItem(null);
      return;
    }
    const todo = (todos || []).find((t) => t.todoId === payload.id);
    setDraggedItem(null);
    if (!todo) return;
    const baseDate = todo.todoDueDate ? dayjs(todo.todoDueDate) : dayjs();
    const supportTaskDefaults = {
      taskDescription: todo.todoDescr ?? '',
      clientId: todo.clientId != null ? String(todo.clientId) : '',
      taskDate: baseDate.format('YYYY-MM-DD'),
      taskStartDate: new Date().toISOString(),
      taskAssignedFrom: user?.id != null ? String(user.id) : '',
      taskAssignedTo: user?.id != null ? String(user.id) : '',
      taskCategoryId: '2',
      taskStatus: '1',
    };
    navigate('/crm_tasks/new', { state: { supportTaskDefaults } });
  };

  const handleDropOnDay = async (targetDay: Dayjs, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverDay(null);
    const payload = getDragData(e);
    if (!payload) return;
    try {
      if (payload.type === 'todo') {
        await updateTodo(payload.id, { todoDueDate: targetDay.startOf('day').toISOString() });
        mutateTodos();
        mutateCompletions1?.();
        mutateCompletions2?.();
      } else {
        const appt = allAppointments.find((a) => a.clientAppId === payload.id);
        if (appt?.clientAppScheduleDateTime) {
          const original = dayjs(appt.clientAppScheduleDateTime);
          const newStart = targetDay.hour(original.hour()).minute(original.minute()).second(0).millisecond(0);
          const duration = appt.clientAppDurationMins ?? 60;
          const newFinish = newStart.add(duration, 'minute');
          await updateAppointment(payload.id, {
            clientAppScheduleDateTime: newStart.toISOString(),
            clientAppFinishDateTime: newFinish.toISOString(),
          });
          mutateAppointments?.();
        }
      }
    } catch {
      mutateTodos();
    } finally {
      setDraggedItem(null);
    }
  };

  const [isResizing, setIsResizing] = useState(false);
  const handleSplitterMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const container = document.querySelector('.todos-weekly-split-container');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitterPosition(Math.max(30, Math.min(80, newPosition)));
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SPLITTER_STORAGE_KEY, String(splitterPosition));
    } catch {
      // ignore
    }
  }, [splitterPosition]);

  if (isLoading) {
    return (
      <div className={cn('flex flex-col h-full', colors.background)}>
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div
              className={cn(
                'inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mb-4',
                colors.primary
              )}
            />
            <div className={cn('font-medium', colors.textSecondary)}>Loading todos...</div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('flex flex-col h-full', colors.background)}>
        <div className="flex items-center justify-center flex-1">
          <div className={cn('text-center border rounded-xl p-6', colors.surface, colors.border)}>
            <div className={cn('font-semibold mb-2', colors.error)}>Error loading todos</div>
            <div className={cn('text-sm', colors.textSecondary)}>{(isError as Error)?.message}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', colors.background)}>
      {!embeddedInCalendar && (
        <div className={cn('shrink-0 border-b', colors.border, colors.surfaceHover)}>
          <div className="px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', colors.info)}>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handlePrevious} className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors', colors.border, colors.interactiveHover)} aria-label="Previous week">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <div className={cn('min-w-[180px] rounded-lg border px-3 py-2 text-center', colors.border, colors.surface)}>
                    <span className={cn('text-base font-semibold', colors.text)}>{weekStart.format('D')}–{weekEnd.format('D MMM YYYY')}</span>
                  </div>
                  <button onClick={handleNext} className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors', colors.border, colors.interactiveHover)} aria-label="Next week">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                  <button onClick={handleToday} className={cn('shrink-0 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors', colors.info, colors.interactiveHover)}>Today</button>
                </div>
              </div>
              <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none">
                <Switch checked={showAppointments} onChange={(e) => setShowAppointments(e.target.checked)} size="sm" aria-label="Show appointments" />
                <span className={cn('text-sm font-medium whitespace-nowrap', colors.text)}>Show appointments</span>
              </label>
              <button onClick={handleNewTodo} className={cn('flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition-all hover:opacity-90', colors.primary, colors.primaryText, 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400/50 dark:focus:ring-gray-500/50 dark:focus:ring-offset-gray-900')}>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                New Todo
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 flex todos-weekly-split-container relative">
        <div
          className="flex flex-col flex-1 min-h-0 p-4 min-w-0"
          style={{ width: `${splitterPosition}%` }}
        >
          <div
            className={cn(
              'h-full flex flex-col min-h-0 overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5 dark:ring-white/5 border',
              colors.surface,
              colors.border
            )}
          >
            <div
              className={cn(
                'grid grid-cols-7 shrink-0 border-b',
                colors.surfaceHover,
                colors.border
              )}
            >
              {weekDays.map((day, idx) => (
                <div
                  key={day.format('YYYY-MM-DD')}
                  className={cn(
                    'py-2 px-2 border-r last:border-r-0 text-center',
                    colors.borderLight
                  )}
                >
                  <div className={cn('text-xs font-semibold uppercase', colors.primaryFg)}>
                    {weekDayLabels[idx]}
                  </div>
                  <div
                    className={cn(
                      'mt-1 inline-flex items-center justify-center w-8 h-8 text-sm font-semibold rounded-lg',
                      day.isSame(dayjs(), 'day')
                        ? cn(colors.primary, colors.primaryText, 'shadow-md')
                        : cn(colors.surfaceHover, colors.text)
                    )}
                  >
                    {day.format('D')}
                  </div>
                </div>
              ))}
            </div>
            <div className={cn('grid grid-cols-7 flex-1 min-h-0 overflow-hidden', colors.surface)}>
              {weekDays.map((day) => {
                const dayItems = getDayItems(day);
                const isToday = day.isSame(dayjs(), 'day');
                const isDragOver = dragOverDay?.isSame(day, 'day');
                return (
                  <div
                    key={day.format('YYYY-MM-DD')}
                    className={cn(
                      'min-h-0 flex flex-col overflow-y-auto border-r last:border-r-0 p-2 transition-colors',
                      colors.borderLight,
                      isToday && cn('ring-inset ring-2', colors.primaryBorder),
                      isDragOver && 'ring-2 ring-inset ring-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20'
                    )}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverDay(day);
                    }}
                    onDragLeave={() => setDragOverDay((d) => (d?.isSame(day, 'day') ? null : d))}
                    onDrop={(e) => handleDropOnDay(day, e)}
                  >
                    <div className="space-y-2">
                      {dayItems.map((item) => {
                        if (item.type === 'appointment') {
                          const a = item.appointment;
                          const title = a.clientAppName || a.client?.clientName || 'Appointment';
                          const timeStr = formatAppointmentTimeRange(a);
                          const isClosed = a.clientAppStatus === 2;
                          return (
                            <div
                              key={`appt-${a.clientAppId}-${day.format('YYYY-MM-DD')}`}
                              role="button"
                              tabIndex={0}
                              draggable
                              onDragStart={(e) => handleDragStart({ type: 'appointment', id: a.clientAppId }, e)}
                              onDragEnd={handleDragEnd}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/appointments/${a.clientAppId}`); } }}
                              className={cn(
                                'w-full rounded-lg border overflow-hidden transition-all duration-150 text-left hover:shadow-sm hover:ring-1',
                                isClosed
                                  ? 'bg-slate-50/90 dark:bg-slate-900/30 border-slate-300 dark:border-slate-700 hover:ring-slate-300 dark:hover:ring-slate-600'
                                  : 'bg-amber-50/90 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50 hover:ring-amber-300 dark:hover:ring-amber-700'
                              )}
                            >
                              <div className="flex items-stretch gap-0 min-h-[28px]">
                                <div
                                  className={cn(
                                    'w-1 shrink-0 rounded-l',
                                    isClosed ? 'bg-slate-500 dark:bg-slate-400' : 'bg-amber-500 dark:bg-amber-400'
                                  )}
                                />
                                <div
                                  className="flex-1 min-w-0 flex items-center gap-1.5 py-1 pl-1 pr-1.5 cursor-pointer"
                                  onClick={() => navigate(`/appointments/${a.clientAppId}`)}
                                >
                                  <span
                                    className={cn(
                                      'shrink-0 flex items-center',
                                      isClosed ? 'text-slate-600 dark:text-slate-300 opacity-80' : 'text-amber-600 dark:text-amber-400'
                                    )}
                                    title="Appointment"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </span>
                                  <div className="flex-1 min-w-0 flex flex-col">
                                    {timeStr && (
                                      <span className={cn('text-[10px] font-medium', colors.textMuted)}>{timeStr}</span>
                                    )}
                                    <span
                                      className={cn(
                                        'text-xs font-medium truncate',
                                        isClosed
                                          ? 'text-emerald-800 dark:text-emerald-200 line-through opacity-90'
                                          : 'text-amber-900 dark:text-amber-100'
                                      )}
                                    >
                                      {title}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className="shrink-0 flex items-center py-1 pr-1.5"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Switch
                                    checked={!isClosed}
                                    onChange={async (e) => {
                                      e.stopPropagation();
                                      const nextStatus = isClosed ? 1 : 2;
                                      await updateAppointment(a.clientAppId, { clientAppStatus: nextStatus });
                                      mutateAppointments?.();
                                    }}
                                    size="sm"
                                    aria-label={isClosed ? 'Open appointment' : 'Close appointment'}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }
                        const { todo, day: itemDay } = item;
                        const isRecurring = !!todo.todoRecurring && !!todo.todoRecurrenceInterval;
                        const occurrenceDone = isRecurring && isRecurrenceCompleted(todo.todoId, itemDay);
                        const completion = isRecurring ? getRecurrenceCompletion(todo.todoId, itemDay) : null;
                        const isDone = occurrenceDone || todo.todoCompleted;
                        const completionInfo =
                          completion?.completedAt || completion?.completedUserDisplayName
                            ? [
                                completion?.completedAt && `C: ${formatDateTime(completion.completedAt)}`,
                                completion?.completedUserDisplayName && `by ${completion.completedUserDisplayName}`,
                              ]
                                .filter(Boolean)
                                .join(' ')
                            : !isRecurring && todo.todoCompleted && todo.todoCompletedAt
                              ? `C: ${formatDateTime(todo.todoCompletedAt)}`
                              : null;
                        const canDragTodo = !isRecurring;
                        return (
                          <div
                            key={`${todo.todoId}-${itemDay.format('YYYY-MM-DD')}`}
                            draggable={canDragTodo}
                            onDragStart={canDragTodo ? (e) => handleDragStart({ type: 'todo', id: todo.todoId }, e) : undefined}
                            onDragEnd={canDragTodo ? handleDragEnd : undefined}
                            className={cn(
                              'rounded-lg border overflow-hidden transition-all duration-150 hover:shadow-sm hover:ring-1',
                              canDragTodo && 'cursor-grab active:cursor-grabbing',
                              isDone
                                ? 'bg-emerald-50/90 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50 hover:ring-emerald-300 dark:hover:ring-emerald-700'
                                : cn(colors.surface, colors.border, 'hover:ring-gray-300 dark:hover:ring-gray-600')
                            )}
                          >
                            <div className="flex items-stretch gap-0 min-h-[28px]">
                              <div
                                className={cn(
                                  'w-1 shrink-0 rounded-l',
                                  isDone ? 'bg-emerald-500 dark:bg-emerald-400' : colors.primary
                                )}
                              />
                              <div className="flex-1 min-w-0 flex items-center gap-1.5 py-1 pl-1 pr-1.5">
                                {(todo.todoType?.icon != null || todo.todoType?.tt_descr) && (
                                  <span
                                    className={cn(
                                      'shrink-0 flex items-center',
                                      isDone ? 'text-emerald-600 dark:text-emerald-400 opacity-80' : colors.primaryFg
                                    )}
                                    title={todo.todoType?.tt_descr}
                                  >
                                    <TodoTypeIcon icon={todo.todoType?.icon} tt_descr={todo.todoType?.tt_descr} className="w-3.5 h-3.5" />
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleTodoClick(todo)}
                                  className={cn(
                                    'flex-1 min-w-0 text-left py-0.5 rounded transition-colors',
                                    isDone
                                      ? 'text-emerald-800 dark:text-emerald-200 line-through opacity-90 hover:opacity-100'
                                      : cn(colors.text, colors.interactiveHover, colors.primaryFg)
                                  )}
                                  title={completionInfo || todo.todoDescr || `Todo #${todo.todoId}`}
                                >
                                  <div className="flex flex-col gap-0.5">
                                    <span className="flex-1 min-w-0 text-xs font-medium truncate">
                                      {todo.todoDescr || `Todo #${todo.todoId}`}
                                    </span>
                                    {todo.client?.clientName && (
                                      <span className={cn('text-[10px] truncate', colors.textSecondary)}>
                                        {todo.client.clientName.length > 15
                                          ? `${todo.client.clientName.slice(0, 15)}…`
                                          : todo.client.clientName}
                                      </span>
                                    )}
                                  </div>
                                </button>
                                <Switch
                                  checked={isRecurring ? !!occurrenceDone : !!todo.todoCompleted}
                                  onChange={(e) =>
                                    isRecurring
                                      ? handleToggleRecurrenceComplete(todo, itemDay, e.target.checked)
                                      : handleToggleComplete(todo, e.target.checked)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  size="sm"
                                  className="shrink-0"
                                  aria-label={isRecurring ? (occurrenceDone ? 'Mark incomplete' : 'Mark complete') : (todo.todoCompleted ? 'Mark incomplete' : 'Mark complete')}
                                />
                              </div>
                            </div>
                            {isDone && completionInfo && (
                              <div
                                className={cn(
                                  'px-2 pb-1 pt-0 text-[10px] truncate border-t border-emerald-200/50 dark:border-emerald-800/30 text-emerald-700/90 dark:text-emerald-300/90 bg-emerald-50/50 dark:bg-emerald-900/10'
                                )}
                                title={completionInfo}
                              >
                                {completionInfo}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div
          className={cn(
            'absolute top-0 bottom-0 w-1 cursor-col-resize transition-colors z-10 shrink-0 opacity-50 hover:opacity-100',
            colors.primary,
            isResizing && '!opacity-100'
          )}
          style={{ left: `${splitterPosition}%`, transform: 'translateX(-50%)' }}
          onMouseDown={handleSplitterMouseDown}
          role="separator"
          aria-label="Resize panels"
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
        </div>

        <div
          className="min-w-0 overflow-y-auto flex flex-col shrink-0"
          style={{ width: `${100 - splitterPosition}%` }}
        >
          <div className={cn('h-full rounded-2xl border overflow-hidden flex flex-col m-4', colors.surface, colors.border)}>
            <div
              className={cn(
                'shrink-0 px-4 py-3 border-b flex items-center gap-3',
                colors.border,
                colors.surfaceHover
              )}
            >
              <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', colors.info)}>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002-2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={cn('font-semibold text-sm', colors.text)}>Unscheduled todos</h3>
              </div>
              {unscheduledTodos.length > 0 && (
                <span className={cn('shrink-0 inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-lg text-xs font-semibold', colors.info)}>
                  {unscheduledTodos.length}
                </span>
              )}
            </div>
            {unscheduledTodos.length > 0 ? (
              <ul className="flex-1 overflow-y-auto space-y-2 p-3">
                {unscheduledTodos.map((todo) => {
                  const isDone = todo.todoCompleted;
                  const completionInfo = isDone && todo.todoCompletedAt ? `C: ${formatDateTime(todo.todoCompletedAt)}` : null;
                  return (
                    <li key={todo.todoId}>
                      <div
                        className={cn(
                          'rounded-lg border overflow-hidden transition-all duration-150 hover:shadow-sm hover:ring-1',
                          isDone
                            ? 'bg-emerald-50/90 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50 hover:ring-emerald-300 dark:hover:ring-emerald-700'
                            : cn(colors.surface, colors.border, 'hover:ring-gray-300 dark:hover:ring-gray-600')
                        )}
                      >
                        <div className="flex items-stretch gap-0 min-h-[36px]">
                          <div
                            className={cn(
                              'w-1 shrink-0 rounded-l',
                              isDone ? 'bg-emerald-500 dark:bg-emerald-400' : colors.primary
                            )}
                          />
                          <div className="flex-1 min-w-0 flex items-center gap-2 py-2 pl-2 pr-3">
                            {(todo.todoType?.icon != null || todo.todoType?.tt_descr) && (
                              <span
                                className={cn(
                                  'shrink-0 flex items-center',
                                  isDone ? 'text-emerald-600 dark:text-emerald-400 opacity-80' : colors.primaryFg
                                )}
                                title={todo.todoType?.tt_descr}
                              >
                                <TodoTypeIcon icon={todo.todoType?.icon} tt_descr={todo.todoType?.tt_descr} className="w-4 h-4" />
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleTodoClick(todo)}
                              className={cn(
                                'flex-1 min-w-0 text-left py-0.5 rounded transition-colors',
                                isDone
                                  ? 'text-emerald-800 dark:text-emerald-200 line-through opacity-90 hover:opacity-100'
                                  : cn(colors.text, colors.interactiveHover, colors.primaryFg)
                              )}
                              title={completionInfo || todo.todoDescr || `Todo #${todo.todoId}`}
                            >
                              <div className="flex flex-col gap-0.5">
                                <span className="flex-1 min-w-0 text-sm font-medium truncate">
                                  {todo.todoDescr || `Todo #${todo.todoId}`}
                                </span>
                                {todo.client?.clientName && (
                                  <span
                                    className={cn(
                                      'text-xs truncate',
                                      colors.textSecondary
                                    )}
                                  >
                                    {todo.client.clientName}
                                  </span>
                                )}
                              </div>
                            </button>
                            <Switch
                              checked={todo.todoCompleted}
                              onChange={(e) => handleToggleComplete(todo, e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                              size="sm"
                              className="shrink-0"
                              aria-label={todo.todoCompleted ? 'Mark incomplete' : 'Mark complete'}
                            />
                          </div>
                        </div>
                        {isDone && completionInfo && (
                          <div
                            className={cn(
                              'px-3 pb-2 pt-0 text-[10px] truncate border-t border-emerald-200/50 dark:border-emerald-800/30 text-emerald-700/90 dark:text-emerald-300/90 bg-emerald-50/50 dark:bg-emerald-900/10'
                            )}
                            title={completionInfo}
                          >
                            {completionInfo}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className={cn('flex-1 flex items-center justify-center p-6 text-sm', colors.textMuted)}>
                No todos without due date or recurrence.
              </div>
            )}
            {/* Drop zone at bottom: drop a todo here to create Support Task */}
            <div
              className={cn(
                'shrink-0 border-t rounded-b-2xl transition-all duration-200 min-h-[52px] flex items-center justify-center gap-2.5 px-4 py-3',
                dragOverSupportTaskZone && draggedItem?.type === 'todo'
                  ? 'bg-amber-50 dark:bg-amber-950/50 border-amber-300 dark:border-amber-700 ring-2 ring-inset ring-amber-400/80'
                  : cn('border-dashed', colors.border, colors.surfaceHover)
              )}
              onDragOver={(e) => {
                if (draggedItem?.type !== 'todo') return;
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'move';
                setDragOverSupportTaskZone(true);
              }}
              onDragLeave={() => setDragOverSupportTaskZone(false)}
              onDrop={handleDropToCreateSupportTask}
            >
              {dragOverSupportTaskZone && draggedItem?.type === 'todo' ? (
                <>
                  <svg className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-semibold text-amber-800 dark:text-amber-200">Release to create Support Task</span>
                </>
              ) : (
                <>
                  <svg className={cn('w-5 h-5 shrink-0', colors.textMuted)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <span className={cn('text-sm font-medium', colors.textMuted)}>Drop a todo here to create Support Task</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodosWeeklyView;
